import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Loader2, FileText, Users, DollarSign, Clock, Building2,
  TrendingUp, Shield, MessageCircle, MapPin, Send,
  Eye, ArrowRight, Upload, BarChart3, CheckCircle, Edit3, Save, X, AlertTriangle,
  Activity, Link2, Lock, CreditCard, Home, GitBranch, Sparkles, Download, AlertCircle, Circle, FileDown, ChevronDown, ShieldCheck, UserCheck, ClipboardList
} from "lucide-react";
import EntityLink from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import DealPacketBuilder from "../components/DealPacketBuilder";
import PhotoGallery from "../components/shared/PhotoGallery";
import { TermSheetComparison } from "../components/shared/DealComparison";
import DocumentUploader from "../components/shared/DocumentUploader";
import { fmtUSD, fmtUSDCompact } from "../utils/currency";
import { formatAddress } from "../utils/addressFormat";
import { SectionHeader, DotLeaderRow, DotLeaderTable, StatusBadge, MapThumbnail, ScoreBadge } from "../components/shared/DesignSystem";
import { FundingProgressBar, DocumentCard } from "../components/shared/OSComponents";
import { DealServicingPanel } from "../components/shared/ServicingPanel";
import ClosingChecklist from "../components/ClosingChecklist";
import DealMapCard from "../components/shared/DealMapCard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { stageLabel } from "../lib/stageConstants";
import { usePageNavigate } from "../hooks/usePageNavigate";

import Deal360Exceptions from "../components/deal360/Deal360Exceptions";
import Deal360Requests from "../components/deal360/Deal360Requests";
import Deal360Messages from "../components/deal360/Deal360Messages";
import CopilotPanel from "../components/deal360/CopilotPanel";
import DealProgressTracker from "../components/deal360/DealProgressTracker";
import RiskBreakdown from "../components/deal360/RiskBreakdown";
import AgentVersionHistory from "../components/deal360/AgentVersionHistory";
import Contact360Drawer from "../components/admin/Contact360Drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

const STAGE_ORDER = ["submitted", "in_review", "underwriting", "approved", "capital_matching", "terms", "funding", "funded"];
const STAGE_LABELS_SHORT = { submitted: "Intake", in_review: "Review", underwriting: "Underwriting", approved: "Approved", capital_matching: "Matching", terms: "Terms", funding: "Funding", funded: "Funded" };
const STAGE_ICONS = { submitted: Home, in_review: Eye, underwriting: Shield, approved: CheckCircle, capital_matching: Users, terms: FileText, funding: DollarSign, funded: CheckCircle };

function riskBadge(band) {
  if (band === "institutional" || band === "strong") return { label: "Low Risk", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (band === "moderate") return { label: "Medium Risk", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "High Risk", cls: "bg-red-50 text-red-700 border-red-200" };
}

function timeAgo(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function Card({ children, className = "", noPad = false, testId }) {
  return (
    <div className={`relative group rounded-2xl overflow-hidden bg-white border border-[#EDE8DC] shadow-[0_2px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(6,95,70,0.08)] hover:border-emerald-200 transition-all duration-300 ${className}`} data-testid={testId}>
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-emerald-600 via-[#E5C98B] to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-l-2xl" />
      <div className={`relative z-10 ${noPad ? "" : "p-6"}`}>{children}</div>
    </div>
  );
}

function CardTitle({ icon: Icon, title, count, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        {Icon && <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200 flex items-center justify-center"><Icon className="h-4 w-4 text-emerald-700" /></div>}
        <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">{title}</h3>
        {count !== undefined && <span className="text-xs text-[#999] font-medium">({count})</span>}
      </div>
      {action || null}
    </div>
  );
}

export default function Deal360Page({ leadId, onBack }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deepTab, setDeepTab] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [editingFees, setEditingFees] = useState(false);
  const [feeForm, setFeeForm] = useState({});
  const [savingFees, setSavingFees] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [editingBrief, setEditingBrief] = useState(false);
  const [briefForm, setBriefForm] = useState({ headline: "", opportunity_summary: "", key_highlights: "", considerations: "" });
  const [savingBrief, setSavingBrief] = useState(false);
  const [showContact360, setShowContact360] = useState(false);
  const [activityCollapsed, setActivityCollapsed] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const [advisorData, setAdvisorData] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);
  const isAdmin = authUser && authUser.role === "admin";
  const role = authUser?.role || "";
  const isBorrower = role === "borrower";
  const canEditFees = ["admin", "originations", "capital_markets"].includes(role);
  const deepTabRef = useRef(null);

  const refreshData = () => {
    axios.get(`${API}/entity/deal/${leadId}/360`, { headers })
      .then(r => setData(r.data))
      .catch(console.error);
  };

  useEffect(() => {
    axios.get(`${API}/entity/deal/${leadId}/360`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    axios.post(`${API}/deals/${leadId}/view`, {}, { headers }).catch(() => {});
  }, [leadId]);

  const openDeepTab = (key) => {
    setDeepTab(deepTab === key ? null : key);
    setTimeout(() => deepTabRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  // Fetch AI summary when underwriting tab is selected
  useEffect(() => {
    if (deepTab !== "underwriting" || !leadId || !token) return;
    axios.get(`${API}/underwriting/deal/${leadId}/ai-summary`, { headers })
      .then(r => { if (r.data.summary) setAiSummary(r.data.summary); })
      .catch(() => {});
  }, [deepTab, leadId]);

  const generateAiSummary = async () => {
    setAiSummaryLoading(true);
    try {
      const res = await axios.post(`${API}/underwriting/deal/${leadId}/ai-summary`, {}, { headers });
      setAiSummary(res.data.summary);
      toast.success("AI analysis generated");
    } catch (err) { console.error(err); toast.error("Failed to generate analysis"); }
    finally { setAiSummaryLoading(false); }
  };

  if (loading) return <div className="min-h-[60vh] bg-[#FBF8F1] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="min-h-[60vh] bg-[#FBF8F1] flex items-center justify-center text-sm text-[#888]">Deal not found</div>;

  const { deal, borrower, underwriting, capital, lenders, documents, audit, servicing, deal_state, activity, requests, intelligence, health_score: healthScore, pending_lender_approval, selected_term_sheet_id, selected_lender_id } = data;
  const dealState = deal_state || {};
  const dealPath = deal.deal_path || "lending";
  const isOpportunityDeal = dealPath === "opportunity" || dealPath === "dual";
  const loanAmount = deal.requested_loan_amount || deal.loan_amount || 0;
  const purchasePrice = deal.purchase_price || 0;
  const ltv = purchasePrice ? ((loanAmount / purchasePrice) * 100).toFixed(0) : 0;
  const riskSummary = dealState.risk_summary;
  const rb = riskSummary ? riskBadge(riskSummary.band) : null;
  const riskScore = intelligence?.risk_score;

  // ── Action handlers ──
  const handleStageClick = async (stage) => {
    if (!window.confirm(`Move deal to "${stageLabel(stage)}"?`)) return;
    try {
      await axios.post(`${API}/admin/leads/${leadId}/stage?stage=${stage}&force=true`, {}, { headers });
      refreshData();
      toast.success(`Stage updated to ${stageLabel(stage)}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update stage"); }
  };

  const startEditing = () => {
    setEditForm({
      full_name: deal.full_name || "", loan_type: deal.loan_type || "",
      property_address: deal.property_address || "", property_type: deal.property_type || "",
      state: deal.state || "", purchase_price: deal.purchase_price || "",
      requested_loan_amount: deal.requested_loan_amount || "", arv: deal.arv || "",
      rehab_budget: deal.rehab_budget || "", exit_strategy: deal.exit_strategy || "",
      fico_estimate: deal.fico_estimate || "", experience_projects_completed: deal.experience_projects_completed || "",
      closing_timeline_days: deal.closing_timeline_days || "", entity_name: deal.entity_name || "",
    });
    setEditMode(true);
    openDeepTab("edit");
  };

  const saveDeal = async () => {
    setSaving(true);
    try {
      const payload = {};
      const numFields = ["purchase_price", "requested_loan_amount", "arv", "rehab_budget", "fico_estimate", "experience_projects_completed", "closing_timeline_days"];
      Object.entries(editForm).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) payload[k] = numFields.includes(k) ? Number(v) : v;
      });
      await axios.put(`${API}/admin/deals/${leadId}/edit`, payload, { headers });
      toast.success("Deal updated");
      setEditMode(false);
      setDeepTab(null);
      refreshData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const loadFeeData = () => {
    if (feeData) return;
    setFeeLoading(true);
    axios.get(`${API}/deals/${leadId}/detail`, { headers })
      .then(r => setFeeData(r.data?.fees || null))
      .catch(() => {})
      .finally(() => setFeeLoading(false));
  };

  const saveFees = async () => {
    setSavingFees(true);
    try {
      await axios.put(`${API}/deals/${leadId}/fees`, feeForm, { headers });
      toast.success("Fee percentages updated");
      setEditingFees(false);
      const r = await axios.get(`${API}/deals/${leadId}/detail`, { headers });
      setFeeData(r.data?.fees || null);
    } catch (err) { toast.error("Failed to save fees"); }
    finally { setSavingFees(false); }
  };

  const handleRecomputeRisk = async () => {
    try {
      const r = await axios.post(`${API}/copilot/v2/recompute-risk?lead_id=${leadId}`, {}, { headers });
      toast.success(r.data?.message || "Risk score recomputed");
      refreshData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to recompute"); }
  };

  const isLender = role === "lender";
  const isInvestor = role === "investor";
  const isFundingStage = ["terms", "funding"].includes(deal.stage);

  // ── Deep-dive tabs ──
  const deepTabs = [
    ...(isFundingStage ? [{ key: "closing", label: "Closing Checklist", icon: ClipboardList }] : []),
    ...(isInternal && pending_lender_approval ? [{ key: "lender_selection", label: "Lender Selection", icon: ShieldCheck, badge: 1 }] : []),
    ...(isInternal ? [{ key: "underwriting", label: "Underwriting", icon: Shield }] : []),
    ...(dealState.visible_modules?.includes("requests") ? [{ key: "requests", label: "Requests", icon: FileText, badge: dealState.open_requests_count }] : []),
    ...(isInternal ? [{ key: "exceptions", label: "Exceptions", icon: AlertTriangle, badge: dealState.open_exceptions_count }] : []),
    ...((isInternal || isBorrower) && (dealState.term_sheet_count > 0 || lenders.length > 0) ? [{ key: "matching", label: `Term Sheets (${lenders.flatMap(l => l.term_sheets || []).length})`, icon: BarChart3 }] : []),
    ...(isOpportunityDeal && (isInternal || isInvestor) ? [{ key: "capital", label: `Capital (${capital.length})`, icon: TrendingUp }] : []),
    { key: "documents_full", label: `All Docs (${documents.length})`, icon: Upload },
    ...(servicing && (isInternal || isBorrower) ? [{ key: "servicing_full", label: "Loan Servicing", icon: CreditCard }] : []),
    ...((isInternal || isBorrower) ? [{ key: "deal_package", label: "Deal Package", icon: Building2 }] : []),
    { key: "photos", label: "Photos", icon: Eye },
    ...(isInternal ? [{ key: "relationships", label: "Relationships", icon: Link2 }] : []),
    ...(isInternal ? [{ key: "audit", label: "Audit", icon: Shield }] : []),
    ...(isInternal ? [{ key: "agent_history", label: "Agent History", icon: GitBranch }] : []),
    ...(isInternal ? [{ key: "notes", label: "Notes", icon: FileText }] : []),
    ...(isAdmin ? [{ key: "revenue", label: "Revenue", icon: DollarSign }] : []),
  ];

  return (
    <div className="min-h-[60vh] bg-[#FBF8F1]" data-testid="deal360-shell">
      <div className="fixed inset-0 opacity-[0.015] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23065F46' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '60px 60px' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 py-6 space-y-6">

        {/* ── 1. HERO HEADER ───────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl border border-emerald-900/20" data-testid="deal360-hero">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(229,201,139,0.08),transparent_60%)]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '60px 60px' }} />
          <div className="relative z-10 px-6 py-5 sm:px-8 sm:py-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                {onBack && <button onClick={onBack} className="text-xs text-white/50 hover:text-white transition-colors mr-2" data-testid="deal360-back">&larr; Back</button>}
                {deal.deal_number && <span className="text-xs font-mono text-[#E5C98B] font-semibold">#{deal.deal_number}</span>}
                <span className="text-xs text-white/40">/</span>
                <span className="text-xs text-white/60 flex items-center gap-1.5"><MapPin className="h-3 w-3 text-[#E5C98B]/50" />{deal.property_address || "—"}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white border border-white/10">{STAGE_LABELS_SHORT[deal.stage] || deal.stage}</span>
                {rb && <span className={`px-3 py-1 rounded-full text-[10px] font-bold border ${rb.cls}`}>{rb.label}</span>}
                {isAdmin && <button onClick={startEditing} className="h-7 w-7 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-white/60 hover:text-[#E5C98B] hover:border-[#E5C98B]/30 transition-all" data-testid="edit-deal-btn"><Edit3 className="h-3 w-3" /></button>}
                {borrower && <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#E5C98B] to-[#8B7340] flex items-center justify-center text-emerald-950 text-xs font-bold shadow-lg">{(borrower.full_name || "?")[0]}</div>}
              </div>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-1">{deal.property_address?.split(",")[0] || "Property"}</h1>
            <p className="text-sm text-white/50">{LOAN_LABELS[deal.loan_type] || deal.loan_type} &middot; {deal.property_type || "Property"} &middot; {borrower?.full_name || "—"}{loanAmount > 0 && <span className="text-[#E5C98B] ml-2 font-bold">{fmtUSDCompact(loanAmount)}</span>}</p>
          </div>
          {/* Map Thumbnail */}
          {deal.property_address && (
            <div className="absolute right-6 top-6 w-40 opacity-80 hover:opacity-100 transition-opacity" data-testid="deal360-map-thumb">
              <DealMapCard address={deal.property_address} lat={deal.extra?.geo_lat} lng={deal.extra?.geo_lng} size="sm" />
            </div>
          )}
        </div>

        {/* ── 2. KPI STRIP ─────────────────────────────────── */}
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-3" data-testid="deal360-kpis">
          {/* Health Score first */}
          {healthScore && (
            <div className={`rounded-2xl bg-white border p-4 text-center transition-all hover:shadow-md ${
              healthScore.band === "excellent" ? "border-emerald-300 shadow-sm" :
              healthScore.band === "good" ? "border-emerald-200" :
              healthScore.band === "fair" ? "border-amber-200" :
              "border-red-200"
            }`} data-testid="kpi-health-score">
              <div className="relative mx-auto w-12 h-12 mb-1">
                <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#f0ece4" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15" fill="none"
                    stroke={healthScore.band === "excellent" ? "#059669" : healthScore.band === "good" ? "#10b981" : healthScore.band === "fair" ? "#f59e0b" : "#ef4444"}
                    strokeWidth="3" strokeDasharray={`${healthScore.score * 0.942} 100`} strokeLinecap="round" />
                </svg>
                <span className={`absolute inset-0 flex items-center justify-center text-sm font-bold ${
                  healthScore.band === "excellent" || healthScore.band === "good" ? "text-emerald-700" :
                  healthScore.band === "fair" ? "text-amber-600" : "text-red-600"
                }`}>{healthScore.score}</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.15em] text-[#999] font-semibold">Health</div>
            </div>
          )}
          {[
            { label: "Loan Amount", value: fmtUSDCompact(loanAmount), accent: true },
            { label: "Docs Complete", value: `${dealState.docs_completeness || 0}%`, warn: (dealState.docs_completeness || 0) < 50 },
            { label: "Photos", value: `${dealState.photo_count || 0}/${dealState.photo_minimum || 5}`, warn: !(dealState.photos_sufficient) },
            { label: "Lenders", value: lenders.length || 0 },
            { label: "Term Sheets", value: dealState.term_sheet_count || 0 },
            { label: "LTV", value: `${ltv}%` },
          ].map((kpi, i) => (
            <div key={i} className={`rounded-2xl bg-white border p-4 text-center transition-all hover:shadow-md hover:border-emerald-200 ${kpi.accent ? "border-emerald-200 shadow-sm" : kpi.warn ? "border-amber-200" : "border-[#EDE8DC]"}`} data-testid={`kpi-${i}`}>
              <div className={`text-xl font-bold tracking-tight tabular-nums ${kpi.accent ? "text-emerald-700" : kpi.warn ? "text-amber-600" : "text-[#0A0A0A]"}`}>{kpi.value}</div>
              <div className="text-[9px] uppercase tracking-[0.15em] text-[#999] font-semibold mt-1">{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* ── 3. STAGE RAIL ─────────────────────────────────── */}
        <Card testId="deal360-stage-rail">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-emerald-700" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Deal Lifecycle</span>
            <span className="text-[10px] text-[#bbb] ml-1">Day {dealState.days_in_stage || 0}{dealState.sla_days ? ` / ${dealState.sla_days}` : ""}</span>
            {dealState.is_overdue && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-bold border border-red-200 flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" /> Overdue</span>}
          </div>
          {(() => {
            const currentStage = deal.stage || "submitted";
            const stageIdx = STAGE_ORDER.indexOf(currentStage);
            const simplified = ["submitted", "underwriting", "capital_matching", "funded"];
            const simplifiedLabels = { submitted: "Intake", underwriting: "Underwriting", capital_matching: "Matching", funded: "Funded" };
            let activeSimplified = 0;
            if (stageIdx >= STAGE_ORDER.indexOf("underwriting")) activeSimplified = 1;
            if (stageIdx >= STAGE_ORDER.indexOf("capital_matching")) activeSimplified = 2;
            if (stageIdx >= STAGE_ORDER.indexOf("funded")) activeSimplified = 3;
            return (
              <div className="flex items-center justify-between max-w-xl mx-auto py-2">
                {simplified.map((stage, i) => {
                  const isActive = i === activeSimplified;
                  const isDone = i < activeSimplified;
                  const Icon = STAGE_ICONS[stage] || CheckCircle;
                  return (
                    <React.Fragment key={stage}>
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => isInternal && handleStageClick(stage)}
                          disabled={!isInternal}
                          className={`h-11 w-11 rounded-full flex items-center justify-center transition-all duration-500 border-2 ${isDone ? "bg-emerald-600 border-emerald-600 text-white shadow-[0_0_12px_rgba(6,95,70,0.25)]" : isActive ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-[0_0_16px_rgba(6,95,70,0.15)]" : "bg-[#f8f6f1] border-[#e0dcd4] text-[#bbb]"} ${isInternal ? "cursor-pointer hover:scale-105" : "cursor-default"}`}
                          data-testid={`stage-node-${stage}`}
                        >
                          {isDone ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-4 w-4" />}
                        </button>
                        <span className={`text-xs mt-2.5 font-semibold tracking-tight ${isActive ? "text-emerald-700" : isDone ? "text-emerald-600" : "text-[#bbb]"}`}>{simplifiedLabels[stage]}</span>
                      </div>
                      {i < simplified.length - 1 && (
                        <div className="flex-1 h-[2px] mx-4 rounded-full relative overflow-hidden bg-[#EDE8DC]">
                          <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full transition-all duration-700" style={{ width: isDone ? "100%" : isActive ? "50%" : "0%" }} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}
        </Card>

        {/* ── 4. DEEP DIVE TABS (moved up, below lifecycle) ── */}
        <div ref={deepTabRef} data-testid="deep-dive-section">
          <div className="flex items-center gap-1 p-1 rounded-xl bg-white border border-[#EDE8DC] overflow-x-auto" data-testid="deep-dive-tabs">
            {deepTabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => openDeepTab(t.key)}
                  data-testid={`deep-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${deepTab === t.key ? "bg-emerald-700 text-white shadow-sm" : "text-[#888] hover:text-[#555] hover:bg-[#FDFCF9]"}`}>
                  <Icon className="h-3.5 w-3.5" /> {t.label}
                  {t.badge > 0 && <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${deepTab === t.key ? "bg-white/20 text-white" : "bg-red-100 text-red-600"}`}>{t.badge}</span>}
                </button>
              );
            })}
          </div>

          {deepTab && (
            <div className="mt-4" data-testid="deep-tab-content">

              {/* ══ CLOSING CHECKLIST TAB ══ */}
              {deepTab === "closing" && isFundingStage && (
                <ClosingChecklist leadId={leadId} token={token} userRole={role} dealNumber={deal.deal_number} onRefresh={refreshData} />
              )}

              {/* ══ LENDER SELECTION APPROVAL TAB (Part 3) ══ */}
              {deepTab === "lender_selection" && isInternal && pending_lender_approval && (
                <LenderSelectionApproval
                  leadId={leadId}
                  selectedTermSheetId={selected_term_sheet_id}
                  selectedLenderId={selected_lender_id}
                  lenders={lenders}
                  deal={deal}
                  token={token}
                  onRefresh={refreshData}
                />
              )}

              {deepTab === "edit" && editMode && (
                <Card testId="edit-form-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-[#0A0A0A]">Edit Deal Details</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={saveDeal} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50" data-testid="save-deal-btn"><Save className="h-3 w-3" />{saving ? "Saving..." : "Save"}</button>
                      <button onClick={() => { setEditMode(false); setDeepTab(null); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200" data-testid="cancel-edit-btn"><X className="h-3 w-3" /> Cancel</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="deal-edit-form">
                    {[
                      { key: "loan_type", label: "Loan Type", type: "select", options: Object.entries(LOAN_LABELS) },
                      { key: "state", label: "State" },
                      { key: "property_address", label: "Property Address" },
                      { key: "property_type", label: "Property Type" },
                      { key: "purchase_price", label: "Purchase Price", type: "number" },
                      { key: "requested_loan_amount", label: "Loan Amount", type: "number" },
                      { key: "arv", label: "ARV", type: "number" },
                      { key: "rehab_budget", label: "Rehab Budget", type: "number" },
                      { key: "exit_strategy", label: "Exit Strategy" },
                      { key: "fico_estimate", label: "FICO", type: "number" },
                      { key: "experience_projects_completed", label: "Experience (projects)", type: "number" },
                      { key: "closing_timeline_days", label: "Timeline (days)", type: "number" },
                    ].map(field => (
                      <div key={field.key} className="p-3 rounded-xl bg-[#FDFCF9] border border-[#EDE8DC]">
                        <label className="text-[9px] text-[#999] uppercase block mb-1.5">{field.label}</label>
                        {field.type === "select" ? (
                          <select value={editForm[field.key] || ""} onChange={e => setEditForm(f => ({ ...f, [field.key]: e.target.value }))} className="w-full text-sm font-semibold text-[#333] bg-white border border-[#EDE8DC] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-200" data-testid={`edit-${field.key}`}><option value="">-</option>{(field.options || []).map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}</select>
                        ) : (
                          <input type={field.type || "text"} value={editForm[field.key] || ""} onChange={e => setEditForm(f => ({ ...f, [field.key]: e.target.value }))} className="w-full text-sm font-semibold text-[#333] bg-white border border-[#EDE8DC] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-200" data-testid={`edit-${field.key}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {deepTab === "underwriting" && isInternal && (
                <Card testId="underwriting-deep">
                  {underwriting ? (
                    <>
                      <h3 className="text-sm font-bold text-[#222] mb-4">Underwriting Analysis</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[{ label: "Decision", value: underwriting.decision || "Pending" }, { label: "Risk Score", value: underwriting.risk_score || "-" }, { label: "Risk Band", value: underwriting.risk_band || "-" }, { label: "Qualified Lenders", value: underwriting.qualified_lenders || "-" }].map((m, i) => (
                          <div key={i} className="p-3 rounded-xl bg-[#FDFCF9] border border-[#EDE8DC] text-center"><div className="text-base font-bold text-[#222]">{m.value}</div><div className="text-[9px] text-[#999] uppercase mt-1">{m.label}</div></div>
                        ))}
                      </div>
                    </>
                  ) : <p className="text-sm text-[#888] text-center py-6">Underwriting scoring not yet available. Use AI Populate below to generate an analysis.</p>}

                  {/* AI Underwriting Summary */}
                  {isAdmin && (
                    <div className="mt-5 rounded-xl border border-purple-200 bg-purple-50/30 p-5 space-y-4" data-testid="underwriting-ai-section">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-[#333] flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-purple-600" /> AI Underwriting Analysis
                          </h4>
                          <p className="text-[10px] text-[#888] mt-0.5">
                            {aiSummary ? `Generated ${aiSummary.generated_at ? new Date(aiSummary.generated_at).toLocaleDateString() : ""}${aiSummary.generated_by_name ? ` by ${aiSummary.generated_by_name}` : ""}` : "Use AI to generate a comprehensive underwriting analysis"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {aiSummary && (
                            <>
                              <button
                                onClick={() => {
                                  const url = `${API}/underwriting/deal/${leadId}/export-pdf`;
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = `IC_Underwriting_${deal.deal_number || leadId.slice(0,8)}.pdf`;
                                  // Use fetch for auth
                                  fetch(url, { headers }).then(r => r.blob()).then(blob => {
                                    const blobUrl = URL.createObjectURL(blob);
                                    a.href = blobUrl;
                                    a.click();
                                    URL.revokeObjectURL(blobUrl);
                                    toast.success("PDF downloaded");
                                  }).catch(() => toast.error("PDF export failed"));
                                }}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#065f46] text-white hover:bg-emerald-700 transition-colors"
                                data-testid="export-pdf-btn"
                              >
                                <FileDown className="h-3.5 w-3.5" /> Export PDF
                              </button>
                              <button
                                onClick={() => {
                                  if (!editingBrief && aiSummary?.lender_brief) {
                                    setBriefForm({
                                      headline: aiSummary.lender_brief.headline || "",
                                      opportunity_summary: aiSummary.lender_brief.opportunity_summary || "",
                                      key_highlights: (aiSummary.lender_brief.key_highlights || []).join("\n"),
                                      considerations: (aiSummary.lender_brief.considerations || []).join("\n"),
                                    });
                                  }
                                  setEditingBrief(!editingBrief);
                                }}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-[#EDE8DC] text-[#333] hover:bg-cream transition-colors"
                                data-testid="edit-lender-brief-btn"
                              >
                                <Edit3 className="h-3.5 w-3.5" /> {editingBrief ? "Cancel" : "Edit Brief"}
                              </button>
                            </>
                          )}
                          <button
                            onClick={generateAiSummary}
                            disabled={aiSummaryLoading}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50 transition-colors"
                            data-testid="generate-ai-summary-btn"
                          >
                            {aiSummaryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                            {aiSummary ? "Regenerate" : "AI Populate"}
                          </button>
                        </div>
                      </div>

                      {aiSummaryLoading && !aiSummary && (
                        <div className="flex items-center justify-center py-8 text-sm text-purple-600 gap-2">
                          <Loader2 className="h-5 w-5 animate-spin" /> Analyzing deal data...
                        </div>
                      )}

                      {aiSummary && (
                        <div className="space-y-4" data-testid="ai-summary-content">
                          <div className="rounded-lg border border-purple-100 bg-white p-4">
                            <div className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold mb-2">Executive Summary</div>
                            <p className="text-sm text-[#333] leading-relaxed">{aiSummary.executive_summary}</p>
                          </div>

                          {aiSummary.recommendation && (
                            <div className={`rounded-lg border p-3 flex items-center gap-3 ${
                              aiSummary.recommendation?.toLowerCase().includes("approve") ? "border-emerald-200 bg-emerald-50" :
                              aiSummary.recommendation?.toLowerCase().includes("decline") ? "border-red-200 bg-red-50" :
                              "border-amber-200 bg-amber-50"
                            }`} data-testid="ai-recommendation">
                              <CheckCircle className={`h-5 w-5 shrink-0 ${
                                aiSummary.recommendation?.toLowerCase().includes("approve") ? "text-emerald-600" :
                                aiSummary.recommendation?.toLowerCase().includes("decline") ? "text-red-600" :
                                "text-amber-600"
                              }`} />
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#999] font-semibold">Recommendation</div>
                                <div className="text-sm font-medium text-[#333]">{aiSummary.recommendation}</div>
                              </div>
                            </div>
                          )}

                          <div className="grid lg:grid-cols-2 gap-3">
                            {aiSummary.strengths?.length > 0 && (
                              <div className="rounded-lg border border-emerald-100 bg-emerald-50/30 p-4">
                                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">Strengths</div>
                                <ul className="space-y-1.5">
                                  {aiSummary.strengths.map((s, i) => (
                                    <li key={i} className="text-xs text-[#333] flex items-start gap-2">
                                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                                      <span>{typeof s === "string" ? s : s.point || JSON.stringify(s)}{typeof s === "object" && s.metric ? <span className="text-[#999] ml-1">({s.metric})</span> : null}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {aiSummary.risks?.length > 0 && (
                              <div className="rounded-lg border border-red-100 bg-red-50/30 p-4">
                                <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-2">Risks</div>
                                <ul className="space-y-1.5">
                                  {aiSummary.risks.map((r, i) => (
                                    <li key={i} className="text-xs text-[#333] flex items-start gap-2">
                                      <AlertCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                                      <span>
                                        {typeof r === "string" ? r : r.point || JSON.stringify(r)}
                                        {typeof r === "object" && r.metric ? <span className="text-[#999] ml-1">({r.metric})</span> : null}
                                        {typeof r === "object" && r.mitigation ? <div className="text-[10px] text-emerald-600 mt-0.5">Mitigation: {r.mitigation}</div> : null}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          {aiSummary.conditions?.length > 0 && (
                            <div className="rounded-lg border border-amber-100 bg-amber-50/30 p-4">
                              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-2">Prior-to-Close Conditions</div>
                              <ul className="space-y-1.5">
                                {aiSummary.conditions.map((c, i) => (
                                  <li key={i} className="text-xs text-[#333] flex items-start gap-2">
                                    <Circle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> {typeof c === "string" ? c : c.condition || JSON.stringify(c)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {aiSummary.suggested_terms && (
                            <div className="rounded-lg border border-[#e8e0d0] bg-[#faf7f2] p-3">
                              <div className="text-[10px] uppercase tracking-wider text-[#999] font-semibold mb-1">Suggested Terms</div>
                              {typeof aiSummary.suggested_terms === "string" ? (
                                <p className="text-xs text-[#555]">{aiSummary.suggested_terms}</p>
                              ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  {Object.entries(aiSummary.suggested_terms).map(([k, v]) => (
                                    <div key={k} className="text-xs">
                                      <span className="text-[#999] capitalize">{k.replace(/_/g, " ")}:</span>{" "}
                                      <span className="font-medium text-[#333]">{typeof v === "string" ? v : JSON.stringify(v)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {aiSummary.ai_fallback && (
                            <div className="text-[10px] text-amber-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" /> Generated using rule-based analysis (AI service unavailable)
                            </div>
                          )}

                          {/* Lender Brief Section */}
                          {aiSummary.lender_brief && !editingBrief && (
                            <div className="rounded-lg border border-[#E5C98B]/40 bg-gradient-to-r from-amber-50/50 to-white p-4" data-testid="lender-brief-display">
                              <div className="text-[10px] uppercase tracking-wider text-[#B8963E] font-semibold mb-2">Lender Brief</div>
                              <p className="text-sm font-semibold text-[#333] mb-1">{aiSummary.lender_brief.headline}</p>
                              <p className="text-xs text-[#555] mb-2">{aiSummary.lender_brief.opportunity_summary}</p>
                              {aiSummary.lender_brief.key_highlights?.length > 0 && (
                                <ul className="space-y-1 mb-2">
                                  {aiSummary.lender_brief.key_highlights.map((h, i) => (
                                    <li key={i} className="text-xs text-[#333] flex items-start gap-1.5"><CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />{typeof h === "string" ? h : h.label ? `${h.label}: ${h.value}` : JSON.stringify(h)}</li>
                                  ))}
                                </ul>
                              )}
                              {aiSummary.lender_brief_edited_by_name && (
                                <div className="text-[9px] text-[#bbb] mt-2">Edited by {aiSummary.lender_brief_edited_by_name} on {aiSummary.lender_brief_edited_at?.slice(0, 10)}</div>
                              )}
                            </div>
                          )}

                          {/* Lender Brief Edit Form */}
                          {editingBrief && (
                            <div className="rounded-lg border border-[#E5C98B]/60 bg-amber-50/30 p-4 space-y-3" data-testid="lender-brief-edit-form">
                              <div className="text-[10px] uppercase tracking-wider text-[#B8963E] font-semibold mb-1">Edit Lender Brief</div>
                              <input
                                className="w-full px-3 py-2 text-sm rounded-lg border border-[#EDE8DC] bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                                placeholder="Headline"
                                value={briefForm.headline}
                                onChange={e => setBriefForm(p => ({ ...p, headline: e.target.value }))}
                                data-testid="brief-headline-input"
                              />
                              <textarea
                                className="w-full px-3 py-2 text-sm rounded-lg border border-[#EDE8DC] bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
                                rows={3}
                                placeholder="Opportunity Summary"
                                value={briefForm.opportunity_summary}
                                onChange={e => setBriefForm(p => ({ ...p, opportunity_summary: e.target.value }))}
                                data-testid="brief-summary-input"
                              />
                              <textarea
                                className="w-full px-3 py-2 text-sm rounded-lg border border-[#EDE8DC] bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
                                rows={3}
                                placeholder="Key Highlights (one per line)"
                                value={briefForm.key_highlights}
                                onChange={e => setBriefForm(p => ({ ...p, key_highlights: e.target.value }))}
                                data-testid="brief-highlights-input"
                              />
                              <textarea
                                className="w-full px-3 py-2 text-sm rounded-lg border border-[#EDE8DC] bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
                                rows={2}
                                placeholder="Considerations (one per line)"
                                value={briefForm.considerations}
                                onChange={e => setBriefForm(p => ({ ...p, considerations: e.target.value }))}
                                data-testid="brief-considerations-input"
                              />
                              <div className="flex gap-2">
                                <button
                                  disabled={savingBrief}
                                  onClick={async () => {
                                    setSavingBrief(true);
                                    try {
                                      const payload = {
                                        lender_brief: {
                                          headline: briefForm.headline,
                                          opportunity_summary: briefForm.opportunity_summary,
                                          key_highlights: briefForm.key_highlights.split("\n").filter(Boolean),
                                          considerations: briefForm.considerations.split("\n").filter(Boolean),
                                        }
                                      };
                                      await axios.patch(`${API}/underwriting/deal/${leadId}/lender-brief`, payload, { headers });
                                      setAiSummary(prev => ({ ...prev, lender_brief: payload.lender_brief, lender_brief_edited_at: new Date().toISOString() }));
                                      setEditingBrief(false);
                                      toast.success("Lender brief updated");
                                    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
                                    finally { setSavingBrief(false); }
                                  }}
                                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#065f46] text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                                  data-testid="save-brief-btn"
                                >
                                  {savingBrief ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Brief
                                </button>
                                <button onClick={() => setEditingBrief(false)} className="px-3 py-2 rounded-lg text-xs font-semibold border border-[#EDE8DC] text-[#666] hover:bg-cream transition-colors">Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {deepTab === "matching" && (
                <Card testId="matching-deep">
                  {(() => {
                    const allTs = lenders.flatMap(l => (l.term_sheets || []).map(ts => ({ ...ts, lender_name: l.full_name, company: l.company, is_selected: l.is_selected, anonymous: l.anonymous })));
                    if (allTs.length >= 1) return (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700/70 flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> Term Sheet Comparison</h4>
                          {allTs.length >= 2 && (
                            <button onClick={() => setShowComparison(!showComparison)}
                              data-testid="toggle-full-comparison"
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                                showComparison ? "bg-emerald-700 text-white" : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                              }`}>
                              <BarChart3 className="h-3 w-3" />
                              {showComparison ? "Hide Full Comparison" : "Full Comparison"}
                            </button>
                          )}
                        </div>

                        {/* Full Comparison Panel */}
                        {showComparison && allTs.length >= 2 && (
                          <div className="mb-5">
                            <TermSheetComparison
                              termSheets={allTs}
                              onClose={() => setShowComparison(false)}
                              onSelect={isBorrower ? async (ts) => {
                                if (!window.confirm("Select this term sheet?")) return;
                                try {
                                  await axios.post(`${API}/term-sheets/${ts.ts_id}/select`, {}, { headers });
                                  toast.success("Lender selected!");
                                  refreshData();
                                  setShowComparison(false);
                                } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                              } : null}
                            />
                          </div>
                        )}

                        {/* Quick Table (always visible) */}
                        {!showComparison && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead><tr className="text-left border-b border-[#EDE8DC]"><th className="pb-2 text-xs uppercase text-[#888] font-medium">Lender</th><th className="pb-2 text-xs uppercase text-[#888] font-medium">Rate</th><th className="pb-2 text-xs uppercase text-[#888] font-medium">Points</th><th className="pb-2 text-xs uppercase text-[#888] font-medium">Amount</th><th className="pb-2 text-xs uppercase text-[#888] font-medium">Term</th><th className="pb-2 text-xs uppercase text-[#888] font-medium">Closing</th><th className="pb-2 text-xs uppercase text-[#888] font-medium">Status</th>{isBorrower && <th className="pb-2 text-xs uppercase text-[#888] font-medium">Action</th>}</tr></thead>
                              <tbody>
                                {allTs.sort((a, b) => (a.interest_rate || 99) - (b.interest_rate || 99)).map((ts, i) => {
                                  const bestRate = Math.min(...allTs.map(t => t.interest_rate || 99));
                                  return (
                                    <tr key={ts.ts_id || i} className="border-t border-[#f0ece4]">
                                      <td className="py-2.5 font-medium text-[#333]"><div className="flex items-center gap-1.5">{ts.anonymous && <Lock className="h-3 w-3 text-[#bbb]" />}{ts.lender_name}{ts.is_selected && <CheckCircle className="h-3 w-3 text-emerald-500" />}</div></td>
                                      <td className={`py-2.5 font-bold ${ts.interest_rate === bestRate ? "text-emerald-600" : "text-[#333]"}`}>{ts.interest_rate}%{ts.interest_rate === bestRate && <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded">Best</span>}</td>
                                      <td className="py-2.5">{ts.points}pts</td>
                                      <td className="py-2.5">{fmtUSD(ts.loan_amount || 0)}</td>
                                      <td className="py-2.5">{ts.term_months}mo</td>
                                      <td className="py-2.5">{ts.closing_days}d</td>
                                      <td className="py-2.5"><span className={`px-2 py-0.5 rounded text-xs ${ts.status === "submitted" ? "bg-blue-50 text-blue-600" : ts.status === "accepted" || ts.status === "selected" ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-600"}`}>{ts.status}</span></td>
                                      {isBorrower && ts.status === "submitted" && <td className="py-2.5"><button onClick={async () => { if (!window.confirm("Select this term sheet?")) return; try { await axios.post(`${API}/term-sheets/${ts.ts_id}/select`, {}, { headers }); toast.success("Selected!"); refreshData(); } catch (err) { toast.error("Failed"); } }} className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`select-ts-${i}`}>Select</button></td>}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* AI Deal Advisor */}
                        <div className="mt-5 pt-4 border-t border-[#EDE8DC]">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-purple-700/70 flex items-center gap-1.5">
                              <Sparkles className="h-3.5 w-3.5" /> AI Deal Advisor
                            </h4>
                            <button
                              onClick={async () => {
                                setAdvisorLoading(true);
                                try {
                                  const r = await axios.post(`${API}/deals/${leadId}/ai-advisor`, {}, { headers });
                                  setAdvisorData(r.data.advisor);
                                  toast.success("AI analysis complete");
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Failed to generate AI analysis");
                                } finally { setAdvisorLoading(false); }
                              }}
                              disabled={advisorLoading}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50 transition-all"
                              data-testid="ai-advisor-btn"
                            >
                              {advisorLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                              {advisorLoading ? "Analyzing..." : advisorData ? "Re-analyze" : "Get AI Recommendation"}
                            </button>
                          </div>

                          {advisorData && (
                            <div className="space-y-4" data-testid="ai-advisor-results">
                              {/* Recommendation Banner */}
                              <div className="rounded-xl bg-gradient-to-r from-purple-50 to-emerald-50 border border-purple-200/50 p-4">
                                <div className="flex items-start gap-3">
                                  <div className="h-9 w-9 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                                    <Sparkles className="h-4 w-4 text-purple-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs font-bold text-purple-700">Recommended: Option #{advisorData.recommended_option}</span>
                                      <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[9px] font-bold">AI PICK</span>
                                    </div>
                                    <p className="text-xs text-[#444] leading-relaxed">{advisorData.recommendation_summary}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Per-Option Scores */}
                              {advisorData.analysis?.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {advisorData.analysis.map((a, i) => {
                                    const isRec = a.lender_index === advisorData.recommended_option;
                                    return (
                                      <div key={i} className={`rounded-xl p-3.5 border transition-all ${isRec ? "bg-emerald-50/60 border-emerald-300" : "bg-[#FDFCF9] border-[#EDE8DC]"}`} data-testid={`advisor-option-${a.lender_index}`}>
                                        <div className="flex items-center justify-between mb-2.5">
                                          <span className="text-xs font-bold text-[#333]">Option #{a.lender_index}</span>
                                          <div className="flex items-center gap-1.5">
                                            {isRec && <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />}
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.score >= 80 ? "bg-emerald-100 text-emerald-700" : a.score >= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{a.score}/100</span>
                                          </div>
                                        </div>
                                        {a.total_cost_estimate && <p className="text-[10px] text-[#666] mb-2">Est. Cost: {a.total_cost_estimate}</p>}
                                        {a.pros?.length > 0 && (
                                          <div className="mb-2">
                                            <span className="text-[9px] font-bold uppercase text-emerald-600 tracking-wider">Strengths</span>
                                            <ul className="mt-1 space-y-0.5">{a.pros.map((p, j) => <li key={j} className="text-[10px] text-[#555] flex items-start gap-1"><CheckCircle className="h-2.5 w-2.5 text-emerald-400 mt-0.5 shrink-0" />{p}</li>)}</ul>
                                          </div>
                                        )}
                                        {a.cons?.length > 0 && (
                                          <div>
                                            <span className="text-[9px] font-bold uppercase text-amber-600 tracking-wider">Considerations</span>
                                            <ul className="mt-1 space-y-0.5">{a.cons.map((c, j) => <li key={j} className="text-[10px] text-[#555] flex items-start gap-1"><AlertCircle className="h-2.5 w-2.5 text-amber-400 mt-0.5 shrink-0" />{typeof c === "string" ? c : c.point || c.text || JSON.stringify(c)}</li>)}</ul>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Key Insights Grid */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {advisorData.comparison_highlights?.length > 0 && (
                                  <div className="rounded-xl bg-[#FDFCF9] border border-[#EDE8DC] p-3.5">
                                    <span className="text-[9px] font-bold uppercase text-blue-600 tracking-wider flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Comparison Highlights</span>
                                    <ul className="mt-2 space-y-1">{advisorData.comparison_highlights.map((h, i) => <li key={i} className="text-[10px] text-[#555]">- {typeof h === "string" ? h : h.text || h.point || JSON.stringify(h)}</li>)}</ul>
                                  </div>
                                )}
                                {advisorData.negotiation_tips?.length > 0 && (
                                  <div className="rounded-xl bg-[#FDFCF9] border border-[#EDE8DC] p-3.5">
                                    <span className="text-[9px] font-bold uppercase text-emerald-600 tracking-wider flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Negotiation Tips</span>
                                    <ul className="mt-2 space-y-1">{advisorData.negotiation_tips.map((t, i) => <li key={i} className="text-[10px] text-[#555]">- {typeof t === "string" ? t : t.text || t.tip || JSON.stringify(t)}</li>)}</ul>
                                  </div>
                                )}
                                {advisorData.risk_factors?.length > 0 && (
                                  <div className="rounded-xl bg-[#FDFCF9] border border-[#EDE8DC] p-3.5">
                                    <span className="text-[9px] font-bold uppercase text-red-600 tracking-wider flex items-center gap-1"><Shield className="h-3 w-3" /> Risk Factors</span>
                                    <ul className="mt-2 space-y-1">{advisorData.risk_factors.map((r, i) => <li key={i} className="text-[10px] text-[#555]">- {typeof r === "string" ? r : r.point || r.text || JSON.stringify(r)}</li>)}</ul>
                                  </div>
                                )}
                                {advisorData.borrower_action_items?.length > 0 && (
                                  <div className="rounded-xl bg-[#FDFCF9] border border-[#EDE8DC] p-3.5">
                                    <span className="text-[9px] font-bold uppercase text-[#8B7340] tracking-wider flex items-center gap-1"><ArrowRight className="h-3 w-3" /> Action Items</span>
                                    <ul className="mt-2 space-y-1">{advisorData.borrower_action_items.map((a, i) => <li key={i} className="text-[10px] text-[#555]">- {typeof a === "string" ? a : a.text || a.action || JSON.stringify(a)}</li>)}</ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    );
                    return <p className="text-sm text-[#888] text-center py-6">No term sheets yet.</p>;
                  })()}
                </Card>
              )}

              {deepTab === "requests" && <Deal360Requests requests={requests || []} leadId={leadId} isInternal={isInternal} onRefresh={refreshData} />}
              {deepTab === "exceptions" && isInternal && <Deal360Exceptions exceptions={data.exceptions || []} leadId={leadId} onRefresh={refreshData} />}

              {deepTab === "capital" && (
                <div className="space-y-4">
                  {capital.length === 0 ? <Card><p className="text-sm text-[#888] text-center py-6">No capital opportunities.</p></Card> : capital.map(opp => (
                    <Card key={opp.opportunity_id}>
                      <div className="flex items-center justify-between mb-3"><EntityLink type="opportunity" id={opp.opportunity_id} label={`Opportunity ${opp.opportunity_id.slice(0, 8)}`} onClick={navigateToEntity} /><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${opp.status === "open" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-600"}`}>{opp.status}</span></div>
                      <FundingProgressBar raised={opp.raised || 0} target={opp.target_raise || 0} label="Capital Progress" className="mb-3" />
                    </Card>
                  ))}
                </div>
              )}

              {deepTab === "servicing_full" && <DealServicingPanel servicing={servicing} isAdmin={isInternal} token={token} leadId={leadId} dealNumber={deal.deal_number} onPaymentRecorded={refreshData} />}
              {deepTab === "documents_full" && (
                <div className="space-y-3">
                  <DocumentUploader leadId={leadId} onUploadComplete={refreshData} />
                  {documents.length === 0 ? <Card><p className="text-sm text-[#888] text-center py-6">No documents.</p></Card> : documents.map(d => <DocumentCard key={d.doc_id} doc={d} onClick={navigateToEntity} />)}
                </div>
              )}
              {deepTab === "deal_package" && <DealPacketBuilder dealId={leadId} isInternal={isInternal} userRole={role} />}
              {deepTab === "photos" && <PhotoGallery leadId={leadId} canUpload={isBorrower || isInternal} />}
              {deepTab === "relationships" && isInternal && (
                <Card testId="relationships-module">
                  <CardTitle icon={Link2} title="Linked Entities" />
                  <div className="space-y-2">
                    {borrower && <div className="flex items-center justify-between p-3 rounded-xl bg-[#FDFCF9] border border-[#EDE8DC]"><div className="flex items-center gap-3"><div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">{borrower.full_name?.charAt(0)}</div><div><EntityLink type="borrower" id={borrower.user_id} label={borrower.full_name} onClick={navigateToEntity} /><div className="text-[10px] text-[#999]">Borrower</div></div></div>{isInternal && <button onClick={() => setShowContact360(true)} className="px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors" data-testid="open-contact360-btn">Contact 360</button>}</div>}
                    {lenders.filter(l => !l.anonymous).map((l, i) => <div key={l.user_id || i} className="flex items-center justify-between p-3 rounded-xl bg-[#FDFCF9] border border-[#EDE8DC]"><div className="flex items-center gap-3"><div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">{l.full_name?.charAt(0)}</div><div><EntityLink type="lender" id={l.user_id} label={l.full_name} onClick={navigateToEntity} /><div className="text-[10px] text-[#999]">{l.is_selected ? "Selected" : "Engaged"} Lender</div></div></div></div>)}
                  </div>
                </Card>
              )}
              {deepTab === "audit" && isInternal && (
                <Card testId="audit-deep">
                  {audit.length === 0 ? <p className="text-sm text-[#888] text-center py-6">No audit events.</p> : (
                    <div className="space-y-1">{audit.map((a, i) => <div key={i} className="flex items-start gap-3 py-2 border-b border-[#f0ece4]"><Clock className="h-3.5 w-3.5 text-[#ccc] mt-0.5 shrink-0" /><div className="flex-1"><p className="text-xs text-[#333]">{a.action}</p><div className="text-[10px] text-[#999] mt-0.5">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</div></div></div>)}</div>
                  )}
                </Card>
              )}
              {deepTab === "notes" && isInternal && <EntityNotes entityType="deal" entityId={leadId} />}
              {deepTab === "agent_history" && isInternal && <AgentVersionHistory leadId={leadId} />}
              {deepTab === "revenue" && isAdmin && (() => {
                if (!feeData && !feeLoading) loadFeeData();
                if (feeLoading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
                if (!feeData) return <Card><p className="text-sm text-[#888] text-center py-6">No fee data available.</p></Card>;
                const f = feeData;
                const estLoan = f.estimated_loan || loanAmount || 0;
                return (
                  <div className="space-y-4" data-testid="revenue-tab">
                    {f.funded ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3"><CheckCircle className="h-6 w-6 text-emerald-500" /><div><div className="text-lg font-bold text-emerald-700">Deal Funded - Revenue Realized</div></div></div> : <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center justify-between"><div className="flex items-center gap-3"><AlertTriangle className="h-6 w-6 text-amber-500" /><div><div className="text-lg font-bold text-amber-700">Projected Revenue</div><div className="text-xs text-amber-600">Based on estimated loan of {fmtUSD(estLoan)}</div></div></div><div className="flex items-center gap-2">{f.fees_is_custom && <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-200" data-testid="custom-fees-badge">Custom</span>}{f.founding_member_discount && <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#E5C98B]/20 text-[#B8943E] border border-[#E5C98B]/30" data-testid="founding-member-fees-badge">Founding Member Rate</span>}{canEditFees && !editingFees && <button onClick={() => { setFeeForm({ origination_fee_pct: f.origination_fee_pct || 1, platform_fee_pct: f.platform_fee_pct || 0.5 }); setEditingFees(true); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="edit-fees-btn"><Edit3 className="h-3 w-3 inline mr-1" />Edit</button>}</div></div>}
                    {editingFees && !f.funded && <Card testId="fee-edit-form"><div className="grid grid-cols-2 gap-4"><div><label className="text-xs font-bold text-[#555] block mb-1.5">Origination Fee %</label><input type="number" step="0.1" min="0" max="10" value={feeForm.origination_fee_pct} onChange={e => setFeeForm(prev => ({ ...prev, origination_fee_pct: parseFloat(e.target.value) }))} className="w-full px-3 py-2.5 rounded-lg border border-[#EDE8DC] text-sm font-semibold focus:ring-2 focus:ring-emerald-200 focus:outline-none" data-testid="origination-pct" /></div><div><label className="text-xs font-bold text-[#555] block mb-1.5">Platform Fee %</label><input type="number" step="0.1" min="0" max="10" value={feeForm.platform_fee_pct} onChange={e => setFeeForm(prev => ({ ...prev, platform_fee_pct: parseFloat(e.target.value) }))} className="w-full px-3 py-2.5 rounded-lg border border-[#EDE8DC] text-sm font-semibold focus:ring-2 focus:ring-emerald-200 focus:outline-none" data-testid="platform-pct" /></div></div><div className="flex gap-2 mt-3"><button onClick={saveFees} disabled={savingFees} className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50" data-testid="save-fees-btn">{savingFees ? "Saving..." : "Save"}</button><button onClick={() => setEditingFees(false)} className="px-4 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200">Cancel</button></div></Card>}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[{ label: f.funded ? "Loan Amount" : "Est. Loan", value: fmtUSD(f.funded ? f.loan_amount : estLoan), accent: false }, { label: f.funded ? "Total Revenue" : "Proj. Revenue", value: fmtUSD(f.funded ? f.total_revenue : f.projected_revenue), accent: true }, { label: `Origination (${f.origination_fee_pct || 1}%)`, value: fmtUSD(f.funded ? f.origination_fee : f.projected_origination) }, { label: `Platform (${f.platform_fee_pct || 0.5}%)`, value: fmtUSD(f.funded ? f.platform_fee : f.projected_platform) }].map((m, i) => <div key={i} className={`rounded-2xl border ${m.accent ? "border-emerald-200 bg-emerald-50" : "border-[#EDE8DC] bg-white"} p-4 text-center`}><div className="text-[10px] uppercase text-[#999] font-bold mb-1">{m.label}</div><div className={`text-xl font-bold ${m.accent ? "text-emerald-700" : "text-[#0A0A0A]"}`}>{m.value}</div></div>)}</div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* ── 5. MAIN CONTENT + COPILOT PANEL ──────────── */}
        <div className={`grid grid-cols-1 ${isInternal ? "lg:grid-cols-12" : ""} gap-6`}>
          {/* LEFT: BENTO GRID */}
          <div className={isInternal ? "lg:col-span-9" : ""}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

          {/* ── BORROWER INFO PANEL (new design system) ────── */}
          {isBorrower && (
            <BorrowerInfoPanel deal={deal} loanAmount={loanAmount} purchasePrice={purchasePrice} lenders={lenders} headers={headers} onRefresh={refreshData} />
          )}

          {/* ── ADMIN INFO PANEL (new design system) ────── */}
          {isInternal && (
            <AdminInfoPanel deal={deal} borrower={borrower} loanAmount={loanAmount} purchasePrice={purchasePrice} lenders={lenders} audit={audit} headers={headers} onRefresh={refreshData} onStageChange={handleStageClick} leadId={leadId} />
          )}

          {/* ── ROW 1: ACTIVITY FEED + MESSAGES side by side ── */}
          <Card className="md:col-span-5" testId="deal360-activity-card">
            <button
              onClick={() => setActivityCollapsed(!activityCollapsed)}
              className="w-full flex items-center justify-between"
              data-testid="activity-feed-toggle"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200 flex items-center justify-center"><Activity className="h-4 w-4 text-emerald-700" /></div>
                <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Activity Feed</h3>
                <span className="text-xs text-[#999] font-medium">({(activity || []).length})</span>
              </div>
              <div className="flex items-center gap-2">
                {(activity || []).length > 0 && activityCollapsed && (
                  <span className="text-[10px] text-emerald-600 font-medium">{(activity || []).length} update{(activity || []).length !== 1 ? "s" : ""}</span>
                )}
                <ChevronDown className={`h-4 w-4 text-[#999] transition-transform duration-200 ${!activityCollapsed ? "rotate-180" : ""}`} />
              </div>
            </button>
            {!activityCollapsed && (
              <div className="space-y-0 max-h-[350px] overflow-y-auto mt-4">
                {(activity || []).slice(0, 10).map((item, i) => {
                  const typeMap = {
                    stage_change: { icon: Activity, cls: "text-purple-600 bg-purple-50 border-purple-200" },
                    timeline: { icon: Clock, cls: "text-blue-600 bg-blue-50 border-blue-200" },
                    message: { icon: MessageCircle, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
                    request: { icon: FileText, cls: "text-amber-600 bg-amber-50 border-amber-200" },
                    document: { icon: Upload, cls: "text-indigo-600 bg-indigo-50 border-indigo-200" },
                    payment: { icon: DollarSign, cls: "text-green-600 bg-green-50 border-green-200" },
                  };
                  const style = typeMap[item.type] || { icon: Activity, cls: "text-gray-600 bg-gray-50 border-gray-200" };
                  const Icon = style.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 py-2.5 border-b border-[#f0ece4] last:border-0">
                      <div className={`h-7 w-7 rounded-xl flex items-center justify-center shrink-0 border ${style.cls}`}><Icon className="h-3.5 w-3.5" /></div>
                      <p className="text-xs text-[#333] leading-relaxed flex-1 truncate">{item.summary || item.detail || "Activity update"}</p>
                      <span className="text-[10px] text-[#bbb] shrink-0 mt-0.5">{timeAgo(item.timestamp)}</span>
                    </div>
                  );
                })}
                {(activity || []).length === 0 && <p className="text-sm text-[#bbb] text-center py-6">No activity yet</p>}
              </div>
            )}
          </Card>

          <Card className="md:col-span-7" noPad testId="deal360-messages-card">
            <Deal360Messages leadId={leadId} />
          </Card>

          {/* ── ROW 2: MISSING DOCS BANNER (full width) ────── */}
          {dealState.missing_docs?.length > 0 && (
            <div className="md:col-span-12 rounded-2xl bg-amber-50 border border-amber-200 p-5" data-testid="missing-docs-banner">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-bold text-amber-700">Missing Documents</span>
                  <span className="text-xs text-amber-600 ml-1">{dealState.missing_docs.length} required</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-amber-600 tabular-nums">{dealState.docs_completeness || 0}% complete</span>
                  <button onClick={() => openDeepTab("documents_full")} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-600 text-white hover:bg-amber-500 transition-colors" data-testid="upload-missing-docs">
                    Upload Docs <ArrowRight className="h-3 w-3 inline ml-1" />
                  </button>
                </div>
              </div>
              <div className="h-2 bg-amber-200 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(dealState.docs_completeness || 0, 2)}%` }} />
              </div>
              <div className="flex flex-wrap gap-2">
                {dealState.missing_docs.map((doc, i) => (
                  <span key={i} className="text-xs px-3 py-1.5 rounded-xl bg-white border border-amber-200 text-amber-700 font-medium">{doc}</span>
                ))}
              </div>
            </div>
          )}

          {/* Carried from Profile notice */}
          {documents.some(d => d.carried_from_profile) && (
            <div className="md:col-span-12 rounded-2xl border p-4" style={{ background: "rgba(64,145,108,0.04)", borderColor: "rgba(64,145,108,0.2)" }} data-testid="profile-docs-carried">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4" style={{ color: "var(--ic-green-dark)" }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ic-green-dark)" }}>Documents Carried from Profile</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {documents.filter(d => d.carried_from_profile).map((doc, i) => (
                  <span key={i} className="text-xs px-3 py-1.5 rounded-xl font-medium flex items-center gap-1.5"
                    style={{ background: "rgba(64,145,108,0.1)", border: "1px solid rgba(64,145,108,0.2)", color: "var(--ic-green-dark)" }}
                    data-testid={`carried-doc-${doc.doc_type || i}`}>
                    <CheckCircle className="h-3 w-3" />
                    {doc.original_filename || doc.doc_type?.replace(/_/g, " ") || "Profile Document"}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── ROW 3: DEAL ECONOMICS / FINANCIALS ────────── */}
          <Card className="md:col-span-12" testId="deal360-financials-card">
            <CardTitle icon={TrendingUp} title="Deal Economics" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              {[
                { label: "Purchase Price", value: purchasePrice ? fmtUSD(purchasePrice) : null },
                { label: "Loan Amount", value: loanAmount ? fmtUSD(loanAmount) : null, accent: true },
                { label: "After Repair Value", value: deal.arv ? fmtUSD(deal.arv) : null },
                { label: "Rehab Budget", value: deal.rehab_budget ? fmtUSD(deal.rehab_budget) : null },
              ].map((m, i) => (
                <div key={i} className={`p-3 rounded-xl text-center ${m.accent ? "bg-emerald-50 border border-emerald-200" : "bg-[#FDFCF9] border border-[#EDE8DC]"}`}>
                  <div className="text-[9px] text-[#999] uppercase tracking-wider font-semibold mb-1">{m.label}</div>
                  {m.value ? (
                    <div className={`text-lg font-bold tabular-nums ${m.accent ? "text-emerald-700" : "text-[#0A0A0A]"}`}>{m.value}</div>
                  ) : (
                    <div className="text-sm italic text-[#bbb]">Not provided</div>
                  )}
                </div>
              ))}
            </div>

            {/* Capital Stack Visualization */}
            {purchasePrice > 0 && (
              <div className="mb-5">
                <div className="text-[10px] uppercase tracking-wider text-[#999] font-bold mb-2">Capital Stack</div>
                <div className="h-8 rounded-full overflow-hidden flex bg-[#f0ece4]">
                  {loanAmount > 0 && <div className="bg-emerald-600 h-full flex items-center justify-center transition-all duration-500" style={{ width: `${Math.min(100, (loanAmount / purchasePrice) * 100)}%` }}>
                    <span className="text-[9px] font-bold text-white px-2 truncate">Debt {ltv}%</span>
                  </div>}
                  <div className="bg-[#E5C98B] h-full flex items-center justify-center flex-1">
                    <span className="text-[9px] font-bold text-emerald-950 px-2 truncate">Equity {100 - Number(ltv)}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Key Ratios */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {[
                { label: "LTV", value: `${ltv}%`, warn: Number(ltv) > 80 },
                { label: "LTC", value: purchasePrice > 0 ? `${((loanAmount / (purchasePrice + (deal.rehab_budget || 0))) * 100).toFixed(0)}%` : null },
                { label: "ARV LTV", value: deal.arv ? `${((loanAmount / deal.arv) * 100).toFixed(0)}%` : null },
                { label: "Profit Potential", value: deal.arv && purchasePrice ? fmtUSDCompact(deal.arv - purchasePrice - (deal.rehab_budget || 0)) : null, accent: true },
                { label: "ROI Est.", value: (() => {
                  const equity = purchasePrice - loanAmount + (deal.rehab_budget || 0);
                  if (!deal.arv || !purchasePrice || equity <= 0) return null;
                  return `${(((deal.arv - purchasePrice - (deal.rehab_budget || 0)) / equity) * 100).toFixed(0)}%`;
                })(), warn: (() => { const eq = purchasePrice - loanAmount + (deal.rehab_budget || 0); return eq <= 0; })(), tooltip: (() => { const eq = purchasePrice - loanAmount + (deal.rehab_budget || 0); return eq <= 0 ? "Loan exceeds purchase price — review LTV." : null; })() },
                { label: "Equity Required", value: (() => {
                  if (purchasePrice <= 0) return null;
                  const eq = purchasePrice - loanAmount;
                  return fmtUSDCompact(eq);
                })(), warn: purchasePrice > 0 && (purchasePrice - loanAmount) < 0, warnLabel: purchasePrice > 0 && (purchasePrice - loanAmount) < 0 ? "Over-leveraged" : null },
              ].map((r, i) => (
                <div key={i} className={`p-2.5 rounded-xl text-center border relative group ${r.warn ? "border-red-200 bg-red-50" : r.accent ? "border-emerald-200 bg-emerald-50" : "border-[#EDE8DC] bg-white"}`}>
                  {r.value ? (
                    <div className={`text-base font-bold tabular-nums ${r.warn ? "text-red-600" : r.accent ? "text-emerald-700" : "text-[#0A0A0A]"}`}>{r.value}</div>
                  ) : (
                    <div className="text-xs italic text-[#bbb]">Not provided</div>
                  )}
                  <div className="text-[8px] text-[#999] uppercase font-semibold mt-0.5">{r.warnLabel || r.label}</div>
                  {r.tooltip && (
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-44 p-2 rounded-lg bg-[#0A0A0A] text-white text-[9px] leading-relaxed shadow-xl z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                      {r.tooltip}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Borrower-visible fee estimate */}
            {isBorrower && loanAmount > 0 && (
              <div className="mt-4 p-3 rounded-xl border border-[#EDE8DC] bg-[#FDFCF9]" data-testid="borrower-fee-estimate">
                <div className="text-[10px] uppercase tracking-wider text-[#999] font-bold mb-2">Estimated Fees</div>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#888]">Origination Fee ({deal.fees?.origination_fee_pct || 1}% of funded amount)</span>
                    <span className="font-semibold text-[#333]">{fmtUSD(loanAmount * (deal.fees?.origination_fee_pct || 1) / 100)}</span>
                  </div>
                  <div className="text-[10px] text-[#aaa] italic">Fees are estimates until the deal funds.</div>
                </div>
                {deal.fees?.founding_member_discount && (
                  <div className="mt-2 px-2 py-1 rounded-lg bg-[#E5C98B]/10 border border-[#E5C98B]/20 flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-[#B8943E]">Founding Member Rate — reduced origination fee applied</span>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* ── ROW 4: LENDER OFFERS ─────────────────────────── */}
          <Card className="md:col-span-7" testId="deal360-lenders-card">
            <CardTitle icon={Users} title="Lender Offers" count={lenders.length}
              action={lenders.flatMap(l => l.term_sheets || []).length > 0 ? (
                <button onClick={() => openDeepTab("matching")} className="text-[10px] font-bold text-emerald-700 hover:text-emerald-600 flex items-center gap-1" data-testid="view-all-terms">
                  Compare All <ArrowRight className="h-3 w-3" />
                </button>
              ) : null}
            />
            {lenders.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {lenders.slice(0, 3).map((lender, i) => {
                  const ts = lender.term_sheets?.[0];
                  return (
                    <div key={lender.user_id || i} className="rounded-xl bg-[#FDFCF9] border border-[#EDE8DC] p-4 hover:border-emerald-200 hover:shadow-md transition-all" data-testid={`lender-offer-${i}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`h-8 w-8 rounded-full border flex items-center justify-center text-xs font-bold ${lender.anonymous ? "bg-gray-50 border-gray-200 text-gray-500" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                          {lender.anonymous ? <Lock className="h-3 w-3" /> : (lender.full_name || "?")[0]}
                        </div>
                        <span className="text-xs text-[#0A0A0A] font-semibold truncate flex-1">{lender.full_name || `Lender ${i + 1}`}</span>
                        {lender.is_selected && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">Selected</span>}
                      </div>
                      {ts ? (
                        <>
                          <div className="text-2xl font-bold text-[#0A0A0A] tracking-tight mb-2">{ts.interest_rate ? `${ts.interest_rate}%` : "—"}</div>
                          <div className="space-y-1.5 mb-3">
                            {[{ l: "Amount", v: fmtUSDCompact(ts.loan_amount) }, { l: "Points", v: ts.points || "—" }, { l: "Close", v: ts.closing_days ? `${ts.closing_days}d` : "—" }].map((r, j) => (
                              <div key={j} className="flex justify-between text-xs"><span className="text-[#999]">{r.l}</span><span className="text-[#333] font-semibold">{r.v}</span></div>
                            ))}
                          </div>
                          {isBorrower && ts.status === "submitted" && (
                            <button onClick={async () => {
                              if (!window.confirm("Select this term sheet? The lender's identity will be revealed.")) return;
                              try { await axios.post(`${API}/term-sheets/${ts.ts_id}/select`, {}, { headers }); toast.success("Lender selected!"); refreshData(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                            }} className="w-full py-2 rounded-full bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors active:scale-95" data-testid={`select-lender-${i}`}>Select <ArrowRight className="h-3 w-3 inline ml-1" /></button>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-3"><div className="text-xs text-[#bbb]">Interest expressed</div><div className="text-sm font-bold text-[#888] mt-1">No terms yet</div></div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[#bbb] text-center py-6">No lender offers yet</p>
            )}
          </Card>

          {/* ── INTELLIGENCE (internal only) ─── */}
          {isInternal && riskScore ? (
            <Card className="md:col-span-5" testId="deal360-intelligence-card">
              <CardTitle icon={Shield} title="Risk Intelligence" />
              <div className="flex items-center gap-6 mb-5 pb-5 border-b border-[#f0ece4]">
                <div className="relative h-16 w-16 shrink-0">
                  <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#f0ece4" strokeWidth="4" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#059669" strokeWidth="4" strokeDasharray={`${(riskScore.overall_score / 100) * 175.93} 175.93`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center"><span className="text-lg font-bold text-[#0A0A0A] tabular-nums">{riskScore.overall_score}</span></div>
                </div>
                <div>
                  <div className="text-base font-bold text-[#0A0A0A] capitalize">{riskScore.risk_band} Risk</div>
                  <div className="text-xs text-[#888]">Confidence: {riskScore.confidence_score}%</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {[
                  { label: "Borrower Strength", value: riskScore.borrower_strength_score, color: "bg-blue-500" },
                  { label: "Deal Quality", value: riskScore.deal_quality_score, color: "bg-emerald-500" },
                  { label: "Market Strength", value: riskScore.market_strength_score, color: "bg-amber-500" },
                  { label: "Execution Risk", value: riskScore.execution_risk_score, color: "bg-purple-500" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-[#555]">{s.label}</span><span className="text-[#0A0A0A] font-bold tabular-nums">{s.value}</span></div>
                    <div className="h-1.5 bg-[#f0ece4] rounded-full overflow-hidden"><div className={`h-full ${s.color} rounded-full`} style={{ width: `${s.value}%` }} /></div>
                  </div>
                ))}
              </div>
              <RiskBreakdown riskScore={riskScore} onRecompute={isAdmin ? handleRecomputeRisk : null} isAdmin={isAdmin} />
            </Card>
          ) : (
            <Card className="md:col-span-5" testId="deal360-docs-summary">
              <CardTitle icon={FileText} title="Documents" count={documents.length}
                action={<button onClick={() => openDeepTab("documents_full")} className="text-[10px] font-bold text-emerald-700 hover:text-emerald-600 flex items-center gap-1" data-testid="view-all-docs">Manage Docs <ArrowRight className="h-3 w-3" /></button>}
              />
              {documents.slice(0, 5).map((doc, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 border-b border-[#f0ece4] last:border-0">
                  <div className="h-7 w-7 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center"><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /></div>
                  <p className="text-xs text-[#333] flex-1 truncate">{doc.file_name || doc.doc_type || "Document"}</p>
                </div>
              ))}
              {documents.length === 0 && <p className="text-sm text-[#bbb] text-center py-4">No documents uploaded</p>}
            </Card>
          )}

          {/* ── SERVICING SUMMARY ── */}
          {servicing && (
            <Card className="md:col-span-5" testId="deal360-servicing-card">
              <CardTitle icon={DollarSign} title="Loan Servicing"
                action={<button onClick={() => openDeepTab("servicing_full")} className="text-[10px] font-bold text-emerald-700 hover:text-emerald-600 flex items-center gap-1" data-testid="full-servicing">Full View <ArrowRight className="h-3 w-3" /></button>}
              />
              <div className="space-y-4">
                {[
                  { icon: CreditCard, label: "Next Payment", value: servicing.monthly_payment_estimate ? fmtUSDCompact(servicing.monthly_payment_estimate) : "—", cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
                  { icon: BarChart3, label: "Loan Balance", value: fmtUSDCompact(servicing.payments?.remaining_principal ?? loanAmount), cls: "text-blue-600 bg-blue-50 border-blue-200", bar: { pct: loanAmount > 0 ? 100 - Math.min(100, ((servicing.payments?.total_paid ?? 0) / loanAmount * 100)) : 100, color: "bg-blue-500" } },
                  { icon: DollarSign, label: "Total Paid", value: fmtUSDCompact(servicing.payments?.total_paid ?? 0), cls: "text-emerald-600 bg-emerald-50 border-emerald-200", bar: { pct: loanAmount > 0 ? Math.min(100, ((servicing.payments?.total_paid ?? 0) / loanAmount * 100)) : 0, color: "bg-emerald-500" } },
                ].map((row, i) => {
                  const Icon = row.icon;
                  return (
                    <div key={i} className="flex items-center gap-4">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center border ${row.cls}`}><Icon className="h-5 w-5" /></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1"><span className="text-sm text-[#555]">{row.label}</span><span className="text-base font-bold text-[#0A0A0A] tabular-nums">{row.value}</span></div>
                        {row.bar && <div className="h-1.5 bg-[#f0ece4] rounded-full overflow-hidden"><div className={`h-full ${row.bar.color} rounded-full`} style={{ width: `${row.bar.pct}%` }} /></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* ── DEAL DETAILS ─── */}
          <Card className={servicing && riskScore ? "md:col-span-7" : "md:col-span-12"} testId="deal360-details-card">
            <CardTitle icon={Building2} title="Deal Details"
              action={isAdmin && !editMode ? <button onClick={startEditing} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors" data-testid="edit-deal-details-btn"><Edit3 className="h-3 w-3" /> Edit</button> : null}
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Loan Type", value: LOAN_LABELS[deal.loan_type] || deal.loan_type },
                { label: "Property", value: deal.property_type },
                { label: "Exit Strategy", value: deal.exit_strategy },
                { label: "Timeline", value: deal.closing_timeline_days ? `${deal.closing_timeline_days} days` : "-" },
                { label: "Purchase Price", value: deal.purchase_price ? fmtUSD(deal.purchase_price) : "-" },
                { label: "ARV", value: deal.arv ? fmtUSD(deal.arv) : "-" },
                { label: "Rehab Budget", value: deal.rehab_budget ? fmtUSD(deal.rehab_budget) : "-" },
                { label: "Experience", value: deal.experience_projects_completed ? `${deal.experience_projects_completed} projects` : "-" },
              ].map((item, i) => (
                <div key={i} className="p-3 rounded-xl bg-[#FDFCF9] border border-[#EDE8DC]">
                  <div className="text-[9px] text-[#999] uppercase tracking-wider font-semibold">{item.label}</div>
                  <div className="text-sm font-semibold text-[#333] mt-1">{item.value || "-"}</div>
                </div>
              ))}
            </div>
          </Card>
            </div>
          </div>

          {/* RIGHT: COPILOT PANEL (3 cols) — Internal only */}
          {isInternal && (
          <div className="lg:col-span-3" data-testid="copilot-sidebar">
            <div className="sticky top-6 space-y-4">
              <CopilotPanel leadId={leadId} onOpenTab={openDeepTab} onNavigate={navigateToEntity} />
            </div>
          </div>
          )}
        </div>

        {/* Borrower progress tracker — shown below main content for borrowers */}
        {isBorrower && <div className="mt-6"><DealProgressTracker leadId={leadId} onOpenTab={openDeepTab} /></div>}

      </div>

      {/* Contact 360 Drawer — admin quick access to borrower contact card */}
      {showContact360 && isInternal && borrower && (
        <Contact360Drawer
          open={showContact360}
          onClose={() => setShowContact360(false)}
          userId={borrower.user_id}
          userType="borrower"
          userName={borrower.full_name}
          onViewDeal={(leadId) => navigateToEntity("deal", leadId)}
          onEntityNavigate={navigateToEntity}
        />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Part 3 — LENDER SELECTION APPROVAL COMPONENT (Admin Deal360)
// ══════════════════════════════════════════════════════════════

function LenderSelectionApproval({ leadId, selectedTermSheetId, selectedLenderId, lenders, deal, token, onRefresh }) {
  const [approving, setApproving] = React.useState(false);
  const [rejecting, setRejecting] = React.useState(false);
  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const headers = { Authorization: `Bearer ${token}` };

  // Find the selected lender's info
  const selectedLender = lenders?.find(l => l.user_id === selectedLenderId);
  const selectedTS = selectedLender?.term_sheets?.find(t => (t.ts_id || t.term_sheet_id) === selectedTermSheetId);

  const handleApprove = async () => {
    if (!window.confirm("Approve this lender selection? Both borrower and lender will be notified, and borrower details will be released to the lender.")) return;
    setApproving(true);
    try {
      const res = await axios.post(`${API_URL}/admin/deals/${leadId}/approve-lender-selection`, {}, { headers });
      toast.success(res.data.message || "Lender selection approved!");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve lender selection");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt("Reason for rejection (optional):", "");
    if (reason === null) return;
    setRejecting(true);
    try {
      const res = await axios.post(`${API_URL}/admin/deals/${leadId}/reject-lender-selection`, { reason: reason || "" }, { headers });
      toast.success(res.data.message || "Lender selection rejected.");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject lender selection");
    } finally {
      setRejecting(false);
    }
  };

  return (
    <Card testId="lender-selection-approval">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
          <ShieldCheck className="h-4 w-4 text-amber-700" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-[#0A0A0A]">Lender Selection Pending Approval</h3>
          <p className="text-[10px] text-[#888]">A borrower has selected a lender for this deal. Review and approve or reject the match.</p>
        </div>
      </div>

      {/* Selected Lender Info */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-3 mb-4" data-testid="selected-lender-info">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Selected Lender</span>
          <span className="px-2 py-0.5 rounded-full text-[8px] font-bold bg-amber-100 text-amber-700 border border-amber-200">Awaiting Approval</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
          <div className="p-2 rounded-lg bg-white border border-amber-100">
            <div className="text-[9px] text-[#999] uppercase">Lender</div>
            <div className="text-xs font-bold text-[#222] mt-0.5">{selectedLender?.full_name || "Unknown"}</div>
            {selectedLender?.company && <div className="text-[9px] text-[#666]">{selectedLender.company}</div>}
          </div>
          {selectedTS && (
            <>
              <div className="p-2 rounded-lg bg-white border border-amber-100">
                <div className="text-[9px] text-[#999] uppercase">Rate</div>
                <div className="text-xs font-bold text-[#222] mt-0.5">{selectedTS.interest_rate}%</div>
              </div>
              <div className="p-2 rounded-lg bg-white border border-amber-100">
                <div className="text-[9px] text-[#999] uppercase">Loan Amount</div>
                <div className="text-xs font-bold text-[#b89d5a] mt-0.5">${selectedTS.loan_amount?.toLocaleString() || "—"}</div>
              </div>
              <div className="p-2 rounded-lg bg-white border border-amber-100">
                <div className="text-[9px] text-[#999] uppercase">Term / Close</div>
                <div className="text-xs font-bold text-[#222] mt-0.5">{selectedTS.term_months || "—"}mo / {selectedTS.closing_days || "—"}d</div>
              </div>
            </>
          )}
        </div>
        {selectedTS?.conditions && (
          <div className="text-[10px] text-amber-700 mt-1">Conditions: {selectedTS.conditions}</div>
        )}
      </div>

      {/* Impact Warning */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3 mb-4" data-testid="approval-impact-warning">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-700 space-y-1">
            <p className="font-semibold">What happens when you approve:</p>
            <ul className="list-disc pl-4 text-[10px] text-blue-600 space-y-0.5">
              <li>Borrower's full name, entity, and contact info will be revealed to the lender</li>
              <li>Both borrower and lender will receive in-app and email notifications</li>
              <li>Other term sheets for this deal will be marked as "Passed"</li>
              <li>Deal stage will advance to "Terms"</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Approve / Reject Buttons */}
      <div className="flex items-center gap-3" data-testid="approval-actions">
        <button
          onClick={handleApprove}
          disabled={approving || rejecting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50"
          data-testid="approve-lender-selection-btn"
        >
          {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
          Approve Lender Selection
        </button>
        <button
          onClick={handleReject}
          disabled={approving || rejecting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 transition-colors disabled:opacity-50"
          data-testid="reject-lender-selection-btn"
        >
          {rejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          Reject Selection
        </button>
      </div>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════════════════
   Borrower Info Panel — New Design System
   Shows LOAN INFO, LOAN DETAILS, DEAL STAGE, TERM SHEET, QUOTES
   ═══════════════════════════════════════════════════════════════ */

const BORROWER_STAGE_ORDER = ["submitted", "in_review", "underwriting", "approved", "capital_matching", "terms", "funding", "funded"];
const BORROWER_STAGE_LABELS = { submitted: "Submitted", in_review: "In Review", underwriting: "Underwriting", approved: "Approved", capital_matching: "Capital Matching", terms: "Terms Received", funding: "Funding", funded: "Funded" };

function BorrowerInfoPanel({ deal, loanAmount, purchasePrice, lenders, headers, onRefresh }) {
  const formatted = formatAddress(deal.property_address || "");
  const ltv = purchasePrice && loanAmount ? ((loanAmount / purchasePrice) * 100).toFixed(1) : "N/A";
  const ltcTotal = (purchasePrice || 0) + (deal.rehab_budget || 0);
  const ltc = ltcTotal > 0 && loanAmount ? ((loanAmount / ltcTotal) * 100).toFixed(1) : "N/A";
  const currentStageIdx = BORROWER_STAGE_ORDER.indexOf(deal.stage || "submitted");

  // Flatten all term sheets from lenders
  const allTermSheets = lenders.flatMap(l =>
    (l.term_sheets || []).map(ts => ({ ...ts, lender_name: l.full_name || `Lender` }))
  );
  const selectedTs = allTermSheets.find(ts => ts.status === "selected") || allTermSheets[0];

  return (
    <div className="md:col-span-12" data-testid="borrower-info-panel">
      <div style={{ display: "grid", gridTemplateColumns: "38% 1fr", gap: 24 }} className="borrower-info-grid">

        {/* LEFT COLUMN — Map + Status */}
        <div>
          <div style={{ borderRadius: "var(--ic-radius-lg)", overflow: "hidden", border: "1px solid var(--ic-border)", marginBottom: 16 }}>
            <MapThumbnail address={deal.property_address} height={200} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <StatusBadge status={stageLabel(deal.stage)} />
          </div>
          {deal.created_at && (
            <div style={{ fontSize: 12, color: "var(--ic-text-secondary)", marginBottom: 8 }}>
              Posted {new Date(deal.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
          <div style={{ display: "inline-block", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: "var(--ic-green-dark)", color: "var(--ic-text-inverse)" }}>
            {LOAN_LABELS[deal.loan_type] || deal.loan_type}
          </div>
        </div>

        {/* RIGHT COLUMN — Sections */}
        <div>
          {/* SECTION 1: LOAN INFO */}
          <SectionHeader title="Loan Info" />
          <DotLeaderRow label="Property Address" value={formatted || "—"} />
          <DotLeaderRow label="Deal ID" value={deal.deal_number || `IC-${deal.lead_id?.substring(0, 8)}`} />
          <DotLeaderRow label="Loan Type" value={LOAN_LABELS[deal.loan_type] || deal.loan_type || "—"} />
          <DotLeaderRow label="Status" value={stageLabel(deal.stage)} />
          {deal.created_at && <DotLeaderRow label="Date Posted" value={new Date(deal.created_at).toLocaleDateString("en-US")} />}

          {/* SECTION 2: LOAN DETAILS */}
          <div style={{ marginTop: 24 }}>
            <SectionHeader title="Loan Details" />
            <DotLeaderRow label="Loan Requested" value={loanAmount ? fmtUSD(loanAmount) : "—"} bold />
            <DotLeaderRow label="Purchase Price" value={purchasePrice ? fmtUSD(purchasePrice) : "—"} />
            <DotLeaderRow label="ARV" value={deal.arv ? fmtUSD(deal.arv) : "—"} />
            <DotLeaderRow label="LTV" value={ltv !== "N/A" ? `${ltv}%` : "N/A"} />
            <DotLeaderRow label="LTC" value={ltc !== "N/A" ? `${ltc}%` : "N/A"} />
            {deal.term_months && <DotLeaderRow label="Term" value={`${deal.term_months} months`} />}
            {deal.closing_timeline_days && <DotLeaderRow label="Close Timeline" value={`${deal.closing_timeline_days} days`} />}
            {deal.rate_min != null && <DotLeaderRow label="Rate Range" value={deal.rate_max ? `${deal.rate_min}% – ${deal.rate_max}%` : `${deal.rate_min}%`} />}
          </div>

          {/* SECTION 3: DEAL STAGE */}
          <div style={{ marginTop: 24 }}>
            <SectionHeader title="Deal Stage" />
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {BORROWER_STAGE_ORDER.map((stage, i) => {
                const isDone = i < currentStageIdx;
                const isActive = i === currentStageIdx;
                const isPending = i > currentStageIdx;
                return (
                  <div key={stage} style={{ display: "flex", alignItems: "flex-start", gap: 12, paddingBottom: i < BORROWER_STAGE_ORDER.length - 1 ? 0 : 0 }}>
                    {/* Dot + connector line */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 16 }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                        background: isDone ? "var(--ic-green-dark)" : isActive ? "var(--ic-gold)" : "var(--ic-border)",
                      }} />
                      {i < BORROWER_STAGE_ORDER.length - 1 && (
                        <div style={{ width: 1, height: 24, background: "var(--ic-border)" }} />
                      )}
                    </div>
                    {/* Label + status */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 0, minHeight: 34 }}>
                      <span style={{
                        fontSize: 13, fontWeight: isActive ? 700 : 500,
                        color: isPending ? "var(--ic-text-secondary)" : "var(--ic-text-primary)",
                      }}>
                        {BORROWER_STAGE_LABELS[stage] || stage}
                      </span>
                      {isActive && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 12, background: "rgba(217,119,6,0.1)", color: "var(--ic-status-pending)" }}>
                          In Progress
                        </span>
                      )}
                      {isDone && (
                        <span style={{ fontSize: 10, color: "var(--ic-green-dark)" }}>
                          <CheckCircle className="h-3 w-3 inline" />
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECTION 4: TERM SHEET (only if exists) */}
          {selectedTs && (
            <div style={{ marginTop: 24 }}>
              <SectionHeader title="Term Sheet" />
              <DotLeaderRow label="Offered Rate" value={selectedTs.interest_rate ? `${selectedTs.interest_rate}%` : "—"} bold />
              <DotLeaderRow label="Points" value={selectedTs.points || "—"} />
              <DotLeaderRow label="Loan Amount" value={selectedTs.loan_amount ? fmtUSD(selectedTs.loan_amount) : "—"} />
              <DotLeaderRow label="Term" value={selectedTs.term_months ? `${selectedTs.term_months} months` : "—"} />
              <DotLeaderRow label="Lender" value={selectedTs.lender_name || "—"} />
              {selectedTs.created_at && <DotLeaderRow label="Issued Date" value={new Date(selectedTs.created_at).toLocaleDateString("en-US")} />}
              {selectedTs.pdf_url && (
                <a href={selectedTs.pdf_url} target="_blank" rel="noopener noreferrer"
                  className="ic-btn-outline" style={{ display: "block", textAlign: "center", marginTop: 12 }}>
                  Download Term Sheet PDF
                </a>
              )}
            </div>
          )}

          {/* SECTION 5: QUOTES RECEIVED */}
          {allTermSheets.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <SectionHeader title="Quotes Received" />
              <DotLeaderTable
                columns={[
                  { key: "lender_name", label: "Lender" },
                  { key: "loan_amount", label: "Amount", render: (r) => r.loan_amount ? fmtUSDCompact(r.loan_amount) : "—" },
                  { key: "interest_rate", label: "Rate", render: (r) => r.interest_rate ? `${r.interest_rate}%` : "—" },
                  { key: "term_months", label: "Term", render: (r) => r.term_months ? `${r.term_months} mo` : "—" },
                  { key: "points", label: "Fee", render: (r) => r.points ? `${r.points} pts` : "—" },
                  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status || "pending"} /> },
                  { key: "action", label: "Action", render: (r) => r.status === "submitted" ? (
                    <button
                      onClick={async () => {
                        if (!window.confirm("Select this term sheet?")) return;
                        try {
                          await axios.post(`${API}/term-sheets/${r.ts_id}/select`, {}, { headers });
                          toast.success("Selected!");
                          onRefresh();
                        } catch (err) { toast.error("Failed"); }
                      }}
                      className="ic-btn-primary" style={{ padding: "4px 12px", fontSize: 11 }}
                      data-testid={`accept-ts-${r.ts_id}`}
                    >
                      Accept
                    </button>
                  ) : null },
                ]}
                rows={allTermSheets}
                emptyText="No quotes submitted yet."
              />
            </div>
          )}
        </div>
      </div>

      {/* Responsive: stack on mobile */}
      <style>{`
        @media (max-width: 768px) {
          .borrower-info-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   Admin Info Panel — New Design System
   LOAN INFO, LOAN DETAILS, DEAL STAGE, ADMIN CONTROLS, QUOTES,
   AUDIT LOG, INTERNAL NOTES
   ═══════════════════════════════════════════════════════════════ */
const ADMIN_STAGE_ORDER = ["submitted", "in_review", "underwriting", "approved", "capital_matching", "terms", "funding", "funded"];
const ADMIN_STAGE_LABELS = { submitted: "Submitted", in_review: "In Review", underwriting: "Underwriting", approved: "Approved", capital_matching: "Capital Matching", terms: "Terms Received", funding: "Funding", funded: "Funded" };

function AdminInfoPanel({ deal, borrower, loanAmount, purchasePrice, lenders, audit, headers, onRefresh, onStageChange, leadId }) {
  const formatted = formatAddress(deal.property_address || "");
  const ltv = purchasePrice && loanAmount ? ((loanAmount / purchasePrice) * 100).toFixed(1) : "N/A";
  const ltcTotal = (purchasePrice || 0) + (deal.rehab_budget || 0);
  const ltc = ltcTotal > 0 && loanAmount ? ((loanAmount / ltcTotal) * 100).toFixed(1) : "N/A";
  const currentStageIdx = ADMIN_STAGE_ORDER.indexOf(deal.stage || "submitted");
  const riskScore = deal.risk_score || deal.summary?.risk_score;

  const allTermSheets = lenders.flatMap(l =>
    (l.term_sheets || []).map(ts => ({ ...ts, lender_name: l.full_name || `Lender` }))
  );
  const auditEntries = (audit || []).slice(0, 10);

  const [noteText, setNoteText] = React.useState("");
  const [savingNote, setSavingNote] = React.useState(false);

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/admin/deals/${leadId}/notes`, { content: noteText.trim(), department: "general" }, { headers });
      setNoteText("");
      toast.success("Note saved");
      onRefresh();
    } catch (err) { toast.error("Failed to save note"); }
    finally { setSavingNote(false); }
  };

  return (
    <div className="md:col-span-12" data-testid="admin-info-panel">
      <div style={{ display: "grid", gridTemplateColumns: "38% 1fr", gap: 24 }} className="admin-info-grid">

        {/* LEFT COLUMN */}
        <div>
          <div style={{ borderRadius: "var(--ic-radius-lg)", overflow: "hidden", border: "1px solid var(--ic-border)", marginBottom: 16 }}>
            <MapThumbnail address={deal.property_address} height={200} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <StatusBadge status={stageLabel(deal.stage)} />
          </div>
          {deal.created_at && (
            <div style={{ fontSize: 12, color: "var(--ic-text-secondary)", marginBottom: 8 }}>
              Posted {new Date(deal.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
          <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: "var(--ic-green-dark)", color: "var(--ic-text-inverse)" }}>
              {LOAN_LABELS[deal.loan_type] || deal.loan_type}
            </span>
          </div>
          {riskScore > 0 && (
            <div style={{ marginTop: 8 }}>
              <ScoreBadge score={riskScore} />
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div>
          {/* SECTION 1: LOAN INFO */}
          <SectionHeader title="Loan Info" />
          <DotLeaderRow label="Property Address" value={formatted || "—"} />
          <DotLeaderRow label="Deal ID" value={deal.deal_number || `IC-${deal.lead_id?.substring(0, 8)}`} />
          <DotLeaderRow label="Loan Type" value={LOAN_LABELS[deal.loan_type] || deal.loan_type || "—"} />
          <DotLeaderRow label="Borrower Name" value={borrower?.full_name || "—"} />
          <DotLeaderRow label="Borrower Email" value={borrower?.email || "—"} />
          <DotLeaderRow label="Status" value={stageLabel(deal.stage)} />
          {deal.created_at && <DotLeaderRow label="Date Posted" value={new Date(deal.created_at).toLocaleDateString("en-US")} />}
          <DotLeaderRow label="Assigned Admin" value={deal.assigned_to || "Unassigned"} />

          {/* SECTION 2: LOAN DETAILS */}
          <div style={{ marginTop: 24 }}>
            <SectionHeader title="Loan Details" />
            <DotLeaderRow label="Loan Requested" value={loanAmount ? fmtUSD(loanAmount) : "—"} bold />
            <DotLeaderRow label="Purchase Price" value={purchasePrice ? fmtUSD(purchasePrice) : "—"} />
            <DotLeaderRow label="ARV" value={deal.arv ? fmtUSD(deal.arv) : "—"} />
            <DotLeaderRow label="LTV" value={ltv !== "N/A" ? `${ltv}%` : "N/A"} />
            <DotLeaderRow label="LTC" value={ltc !== "N/A" ? `${ltc}%` : "N/A"} />
            {deal.term_months && <DotLeaderRow label="Term" value={`${deal.term_months} months`} />}
            {deal.closing_timeline_days && <DotLeaderRow label="Close Timeline" value={`${deal.closing_timeline_days} days`} />}
            {deal.rate_min != null && <DotLeaderRow label="Rate Range" value={deal.rate_max ? `${deal.rate_min}% – ${deal.rate_max}%` : `${deal.rate_min}%`} />}
          </div>

          {/* SECTION 3: DEAL STAGE */}
          <div style={{ marginTop: 24 }}>
            <SectionHeader title="Deal Stage" />
            <div style={{ display: "flex", flexDirection: "column" }}>
              {ADMIN_STAGE_ORDER.map((stage, i) => {
                const isDone = i < currentStageIdx;
                const isActive = i === currentStageIdx;
                return (
                  <div key={stage} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 16 }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                        background: isDone ? "var(--ic-green-dark)" : isActive ? "var(--ic-gold)" : "var(--ic-border)",
                      }} />
                      {i < ADMIN_STAGE_ORDER.length - 1 && <div style={{ width: 1, height: 24, background: "var(--ic-border)" }} />}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 34 }}>
                      <span style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: i > currentStageIdx ? "var(--ic-text-secondary)" : "var(--ic-text-primary)" }}>
                        {ADMIN_STAGE_LABELS[stage]}
                      </span>
                      {isActive && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 12, background: "rgba(217,119,6,0.1)", color: "var(--ic-status-pending)" }}>In Progress</span>}
                      {isDone && <CheckCircle className="h-3 w-3" style={{ color: "var(--ic-green-dark)" }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECTION 4: ADMIN CONTROLS */}
          <div style={{ marginTop: 24 }}>
            <SectionHeader title="Admin Controls" />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={() => onStageChange("approved")} className="ic-btn-primary" style={{ width: "100%", padding: 12 }} data-testid="admin-approve-deal">
                Approve Deal
              </button>
              <button onClick={() => onStageChange("needs_info")} className="ic-btn-outline" style={{ width: "100%", padding: 12, borderColor: "var(--ic-green-mid)", color: "var(--ic-green-mid)" }} data-testid="admin-request-info">
                Request More Info
              </button>
              <button onClick={() => onStageChange("declined")} className="ic-btn-outline" style={{ width: "100%", padding: 12, borderColor: "#DC2626", color: "#DC2626" }} data-testid="admin-reject-deal">
                Reject Deal
              </button>
              <button className="ic-btn-outline" style={{ width: "100%", padding: 12, borderColor: "#D97706", color: "#D97706" }} data-testid="admin-flag-mc">
                Flag for Maker-Checker
              </button>
            </div>
          </div>

          {/* SECTION 5: QUOTES & TERM SHEETS */}
          <div style={{ marginTop: 24 }}>
            <SectionHeader title="Quotes & Term Sheets" />
            <DotLeaderTable
              columns={[
                { key: "lender_name", label: "Lender" },
                { key: "loan_amount", label: "Amount", render: (r) => r.loan_amount ? fmtUSDCompact(r.loan_amount) : "—" },
                { key: "interest_rate", label: "Rate", render: (r) => r.interest_rate ? `${r.interest_rate}%` : "—" },
                { key: "term_months", label: "Term", render: (r) => r.term_months ? `${r.term_months} mo` : "—" },
                { key: "points", label: "Fee", render: (r) => r.points ? `${r.points} pts` : "—" },
                { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status || "pending"} /> },
                { key: "action", label: "Action", render: (r) => (
                  <button onClick={() => {}} className="ic-btn-outline" style={{ padding: "4px 12px", fontSize: 12, flex: "none" }}>VIEW</button>
                )},
              ]}
              rows={allTermSheets}
              emptyText="No quotes submitted yet."
            />
          </div>

          {/* SECTION 6: AUDIT LOG */}
          {auditEntries.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <SectionHeader title="Audit Log" />
              <DotLeaderTable
                columns={[
                  { key: "date", label: "Date", render: (r) => r.timestamp ? new Date(r.timestamp).toLocaleDateString("en-US") : (r.created_at ? new Date(r.created_at).toLocaleDateString("en-US") : "—") },
                  { key: "action", label: "Action", render: (r) => r.action || r.event_type || r.type || "—" },
                  { key: "actor", label: "Actor", render: (r) => r.actor_name || r.user_name || r.performed_by || "System" },
                  { key: "before", label: "Before", render: (r) => r.before_value || r.old_value || "—" },
                  { key: "after", label: "After", render: (r) => r.after_value || r.new_value || "—" },
                ]}
                rows={auditEntries}
              />
            </div>
          )}

          {/* SECTION 7: INTERNAL NOTES */}
          <div style={{ marginTop: 24 }}>
            <SectionHeader title="Internal Notes" />
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add internal note visible only to admins..."
              style={{
                width: "100%", minHeight: 80, padding: 12, fontSize: 13,
                background: "var(--ic-bg-row-alt)", border: "1px solid var(--ic-border)",
                borderRadius: "var(--ic-radius)", resize: "vertical", fontFamily: "inherit",
                outline: "none",
              }}
              data-testid="admin-note-textarea"
            />
            <button
              onClick={saveNote}
              disabled={savingNote || !noteText.trim()}
              className="ic-btn-primary"
              style={{ width: "100%", marginTop: 8, padding: 10 }}
              data-testid="admin-save-note"
            >
              {savingNote ? "Saving..." : "Save Note"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .admin-info-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
